/**
 * OGC request params that ArcGIS WFSLayer adds itself. Strip these when an
 * admin pastes a full GetCapabilities / GetFeature URL so we don't duplicate
 * them on every request.
 */
const OGC_REQUEST_PARAMS = new Set([
  'service',
  'version',
  'request',
  'typename',
  'typenames',
  'outputformat',
  'srsname',
  'bbox',
  'filter',
  'cql_filter',
  'resulttype',
  'count',
  'startindex',
  'sortby',
  'propertyname',
]);

export type ParsedWfsServiceUrl = {
  /** Base WFS endpoint with query string removed. */
  url: string;
  /** Non-OGC params (e.g. authkey) appended to every WFS request. */
  customParameters?: Record<string, string>;
  /** Feature type from a GetFeature URL, if present. */
  featureTypeName?: string;
};

/**
 * Normalize a WFS endpoint for ArcGIS `WFSLayer`.
 *
 * Admins often paste a GetCapabilities URL like:
 * `https://host/geoserver/ows?service=WFS&version=2.0.0&request=GetCapabilities&authkey=…`
 *
 * ArcGIS expects the bare OWS URL plus auth via `customParameters`.
 */
export function parseWfsServiceUrl(rawUrl: string): ParsedWfsServiceUrl {
  if (!rawUrl) {
    return { url: rawUrl };
  }

  let parsed: URL;
  try {
    parsed = new URL(rawUrl);
  } catch {
    return { url: rawUrl };
  }

  const featureTypeName =
    parsed.searchParams.get('typeNames') ||
    parsed.searchParams.get('typenames') ||
    parsed.searchParams.get('typeName') ||
    parsed.searchParams.get('typename') ||
    undefined;

  const customParameters: Record<string, string> = {};
  parsed.searchParams.forEach((value, key) => {
    if (!OGC_REQUEST_PARAMS.has(key.toLowerCase())) {
      customParameters[key] = value;
    }
  });

  // Drop the query string; ArcGIS rebuilds WFS requests from the base URL.
  parsed.search = '';
  parsed.hash = '';

  return {
    url: parsed.toString().replace(/\/$/, ''),
    ...(Object.keys(customParameters).length > 0 ? { customParameters } : {}),
    ...(featureTypeName ? { featureTypeName } : {}),
  };
}
